/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, RouterLink, UrlSerializer } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { OurAppTrackerService } from '@src/app/shared/our-app-tracker-service';
import { TenantService } from '@src/app/shared/tenant.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';

@Component({
    selector: 'app-invalid-navigation',
    templateUrl: './invalid-navigation.component.html',
    styleUrl: './invalid-navigation.component.scss',
    imports: [RouterLink, MatButtonModule, MatIconModule],
})
export class InvalidNavigationComponent implements OnInit {

    router = inject(Router);

    readonly badUrl = window.location.href;
    readonly badPath = window.location.pathname;
    readonly timestamp = new Date().toLocaleString('en-IE');
    private tracker = inject(OurAppTrackerService);
    readonly events = this.tracker.getEvents();
    readonly isAidan = this.badUrl.includes('9c752724-cc3f-45c9-b70b-0666f4924c36');
    readonly isPwa = this.detectPwa();
    readonly likelyCause = this.detectLikelyCause();
    copied = false;

    private http = inject(HttpClient);
    private currentUserService = inject(CurrentUserService);
    serializer = inject(UrlSerializer);

    ngOnInit() {
        if (this.tryMangledUrlRedirect()) { return; }
        if (this.tryOldSchemaRedirect()) { return; }
        this.logBadNavigation();
    }

    private tryMangledUrlRedirect(): boolean {
        if (!/%3B|%3A|%2F|%3D/i.test(this.badUrl)) {
            return false;
        }
        const rawRelativePath = window.location.pathname + window.location.search + window.location.hash;
        const cleanRelativePath = rawRelativePath
            .replace(/%3B/gi, ';')
            .replace(/%3A/gi, ':')
            .replace(/%2F/gi, '/')
            .replace(/%3D/gi, '=');
        this.tracker.logAction(`Invalid URL Rerouted From [${rawRelativePath}] to [${cleanRelativePath}]`);
        this.router.navigateByUrl(this.serializer.parse(cleanRelativePath));
        return true;
    }

    private tryOldSchemaRedirect(): boolean {
        const oldSchemaPrefixes = ['/myRequests', '/social/', '/crm/', '/budgets/'];
        if (!oldSchemaPrefixes.some(p => this.badPath.startsWith(p))) {
            return false;
        }
        const teamId = this.currentUserService.currentUser?.current_team_id;
        if (!teamId) {
            return false;
        }
        const target = '/' + teamId + this.badPath + window.location.search;
        this.tracker.logAction(`Invalid URL Rerouted From [${this.badPath}${window.location.search}] to [${target}]`);
        this.router.navigateByUrl(target);
        return true;
    }

    private detectPwa(): boolean {
        return window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as { standalone?: boolean }).standalone === true;
    }

    private detectLikelyCause(): 'pwa' | 'bookmark' | 'email' | 'unknown' {
        if (this.isPwa) {
            return 'pwa';
        }
        if (window.location.search.includes('topTab=')) {
            return 'bookmark';
        }
        const knownAppPaths = ['crm/', 'myRequests', 'social/', 'budgets/', 'settings/', 'txn/'];
        if (knownAppPaths.some(p => this.badPath.includes(p))) {
            return 'email';
        }
        return 'unknown';
    }

    private logBadNavigation() {
        const hasSucceededNavigation = this.events.some(
            e => e.startsWith('Navigating to') && e.includes('SUCCEEDED')
        );
        if (!hasSucceededNavigation) {
            return;
        }
        const url = TenantService.bareAjaxPath + 'logError';
        const data = {
            message: `[Bad Link] ${this.badUrl}`,
            pathName: this.badPath,
            fullPath: this.badUrl,
            stackTrace: `Navigated to invalid URL at ${this.timestamp}\n` + this.events.join('<br/>\n'),
        };
        this.http.post(url, data).pipe(catchError(() => of(null))).subscribe();
    }

    getDiagnosticText() {
        return [
            'OurOMC — Invalid Navigation Report',
            `Time: ${this.timestamp}`,
            `URL attempted: ${this.badUrl}`,
            `Running as installed app (PWA): ${this.isPwa}`,
            '',
            'Navigation history:',
            ...this.events.map(e => `  ${e}`),
        ].join('\n');
    }

    get emailHref() {
        const subject = encodeURIComponent('OurOMC Bad Link Report');
        const body = encodeURIComponent(this.getDiagnosticText());
        return `mailto:support@ouromc.ie?subject=${subject}&body=${body}`;
    }

    async copyDiagnostic() {
        await navigator.clipboard.writeText(this.getDiagnosticText());
        this.copied = true;
        setTimeout(() => this.copied = false, 3000);
    }
}
